import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

/** In-page camera keeps the call in its foreground browser activity. */
export default function AgentCameraDialog({ open, owner, currentOwner, onClose, onCapture, onOpen }) {
  const video = useRef(null);
  const generation = useRef(0);
  const streamRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const id = ++generation.current;
    const releaseVoice = onOpen();
    setPhoto(null); setError(null); setReady(false); setSaving(false);
    const stop = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop()); streamRef.current = null;
      if (video.current) video.current.srcObject = null;
    };
    const acquire = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (generation.current !== id || currentOwner() !== owner || document.visibilityState !== 'visible') {
          stream.getTracks().forEach((track) => track.stop()); return;
        }
        streamRef.current = stream; video.current.srcObject = stream;
        await video.current.play();
        if (generation.current !== id || currentOwner() !== owner || document.visibilityState !== 'visible') {
          if (streamRef.current === stream) stop(); else stream.getTracks().forEach((track) => track.stop());
          return;
        }
        setReady(true);
      } catch (_) {
        if (generation.current === id) { stop(); setError('The camera could not start. You can attach an existing image instead.'); }
      }
    };
    const visible = () => { if (document.visibilityState !== 'visible') { stop(); onClose(); } };
    document.addEventListener('visibilitychange', visible); acquire();
    return () => {
      generation.current += 1; document.removeEventListener('visibilitychange', visible); stop(); releaseVoice?.();
    };
    // Callbacks are the current owner's open-generation boundary, not a camera restart trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, owner]);

  const capture = () => {
    const source = video.current;
    if (!ready || !source?.videoWidth || !source.videoHeight) return;
    const id = generation.current;
    const canvas = document.createElement('canvas');
    const scale = Math.min(1, 2048/Math.max(source.videoWidth, source.videoHeight));
    canvas.width = Math.round(source.videoWidth*scale); canvas.height = Math.round(source.videoHeight*scale);
    canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob || generation.current !== id || currentOwner() !== owner) return;
      setPhoto(blob);
    }, 'image/jpeg', 0.92);
  };
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!photo) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(photo); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  return <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="agent-camera-title">
    <DialogTitle id="agent-camera-title">Share a photo</DialogTitle>
    <DialogContent>
      {error && <Alert severity="warning">{error}</Alert>}
      <Box component="video" ref={video} playsInline muted sx={{ width: '100%', display: photo ? 'none' : 'block' }} aria-label="Camera view" />
      {preview && <Box component="img" src={preview} alt="Photo to attach" sx={{ width: '100%' }} />}
    </DialogContent>
    <DialogActions sx={{ flexWrap: 'wrap' }}>
      <Button disabled={saving} onClick={onClose}>Cancel photo</Button>
      {photo ? <>
        <Button disabled={saving} onClick={() => setPhoto(null)}>Retake</Button>
        <Button disabled={saving} onClick={async () => {
          if (currentOwner() !== owner) return;
          const id = generation.current; setSaving(true); setError(null);
          try {
            const accepted = await onCapture(new File([photo], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' }));
            if (generation.current === id && accepted !== false) onClose();
            else if (generation.current === id) setError('The photo could not be attached. Retry or cancel.');
          } catch (_) { if (generation.current === id) setError('The photo could not be attached. Retry or cancel.'); }
          finally { if (generation.current === id) setSaving(false); }
        }}>{saving ? 'Attaching…' : 'Use photo'}</Button>
      </> : <Button disabled={!ready} onClick={capture}>Take photo</Button>}
    </DialogActions>
  </Dialog>;
}
